import {
  type CreateReference,
  LocationTypeCode,
} from '@quromedical/fhir-common';
import { type CreateLocation, CreateLocationSchema } from '@quromedical/models';
import { Row } from 'components/base';
import { CrudForm, type SubmissionHandler } from 'components/form';
import { mapReferenceToSelectOption } from 'core/forms';
import type { OrganizationFetcher } from 'core/group';
import { Alert, Skeleton, SkeletonProvider, Snackbar } from 'design-system';
import { router } from 'expo-router';
import { logger } from 'helpers';
import { useObjectState } from 'hooks/useObjectState';
import { useRevalidation } from 'hooks/useRevalidation';
import { LocationApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useState } from 'react';
import { strings } from 'strings';

import { createFields } from './fields';

interface CreateFormProps {
  organizationFetcher: OrganizationFetcher;
  initialOrganization?: CreateReference;
}

export const LocationCreateCard: React.FC<CreateFormProps> = ({
  organizationFetcher,
  initialOrganization,
}) => {
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const formData = useObjectState<Partial<CreateLocation>>({});
  const revalidate = useRevalidation('Location');

  const initialValues = {};

  const fields = createFields(
    organizationFetcher,
    formData.value.type || LocationTypeCode.VirtualWard,
    mapReferenceToSelectOption(initialOrganization),
  );

  const onSubmit = useCallback<SubmissionHandler<CreateLocation>>(
    async (formData: CreateLocation) => {
      setLoading(true);

      try {
        const api = new LocationApi();

        const result = await api.createLocation({ ...formData });
        router.push({ pathname: `/admin/location/${result.id || ''}` });

        await revalidate();
        setLoading(false);
        return {};
      } catch (error) {
        logger.error(error);
        setHasError(true);
        return { error };
      }
    },
    [revalidate],
  );

  const onAlertClose = useCallback(() => {
    setHasError(false);
  }, []);

  return (
    <SkeletonProvider loading={loading}>
      <Row alignItems="center" justifyContent="space-between" unsetZIndex>
        <Skeleton>
          <CrudForm
            title={strings.LocationDetails}
            fields={fields}
            showSubmitButton={true}
            initialValues={initialValues}
            validationSchema={CreateLocationSchema}
            onSubmit={onSubmit}
            onChange={(data) => {
              formData.set('type')(data.type);
            }}
          />
        </Skeleton>
        <Snackbar isOpen={hasError} onClose={onAlertClose}>
          <Alert
            actionIcon="close"
            backgroundColor="status-critical"
            title={strings.AlertError}
            onAction={onAlertClose}
            body={strings.LocationCreateError}
          />
        </Snackbar>
      </Row>
    </SkeletonProvider>
  );
};
